import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, Clock, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { Mascot } from '../Mascot';
import { heroContent, heroStats } from '../../lib/marketingContent';

export const HeroSection = ({ onCtaClick }) => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  const handleStart = () => {
    navigate(user ? '/dashboard' : '/quiz-aides');
    onCtaClick && onCtaClick();
  };

  const handleOpenCopilot = () => {
    navigate(user ? '/dashboard' : '/login?next=/dashboard');
  };

  const handleBusiness = () => {
    document.getElementById('business')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      ref={sectionRef}
      id="hero"
      className={`relative min-h-screen flex items-center justify-center overflow-hidden ${
        isDark ? 'bg-black' : 'bg-white'
      }`}
      data-testid="hero-section"
    >
      {/* Subtle gradient - Apple style */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full filter blur-[150px] ${
          isDark ? 'bg-[#81d8d0]/8' : 'bg-[#81d8d0]/15'
        }`} />
      </div>

      <motion.div 
        className="relative z-10 w-full max-w-[980px] mx-auto px-6 text-center pt-24 md:pt-28"
        style={{ opacity, scale }}
      >
        {/* Top badge - Teasing the test */}
        <motion.div
          className="flex justify-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-[11px] sm:text-[13px] tracking-[-0.01em] ${
            isDark 
              ? 'bg-[#81d8d0]/10 text-[#81d8d0] border border-[#81d8d0]/20' 
              : 'bg-[#81d8d0]/10 text-[#81d8d0] border border-[#81d8d0]/20'
          }`}>
            <Sparkles size={14} strokeWidth={1.5} className="hidden sm:block" />
            <span>{heroContent.eyebrow}</span>
            <span className="font-semibold">→ experience chat</span>
          </div>
        </motion.div>

        {/* Mascot - centered, smaller, elegant */}
        <motion.div
          className="flex justify-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Mascot size={72} />
        </motion.div>

        {/* Headline - Direct value proposition */}
        <motion.h1
          className={`text-[40px] sm:text-[52px] md:text-[64px] lg:text-[72px] font-semibold tracking-[-0.03em] leading-[1.05] mb-4 ${
            isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
          }`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className={isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'}>Votre copilote</span>
          <br />
          <span className="text-[#81d8d0]">administratif.</span>
        </motion.h1>

        {/* Subheadline - Apple style */}
        <motion.p
          className={`text-[17px] sm:text-[19px] md:text-[21px] leading-[1.4] font-normal tracking-[-0.016em] max-w-[640px] mx-auto mb-8 ${
            isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {heroContent.subtitle}
          <span className={`font-medium ${isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'}`}> Vous gardez le controle.</span>
        </motion.p>

        {/* Primary CTA - Results-oriented */}
        <motion.div
          className="flex flex-col items-center gap-4 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleStart}
              data-testid="hero-cta-primary"
              className="btn-apple btn-apple-primary group text-[17px] px-8 py-4"
            >
              {heroContent.primaryCta}
              <ArrowRight size={18} strokeWidth={1.5} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleOpenCopilot}
              data-testid="hero-cta-secondary"
              className="btn-apple btn-apple-secondary text-[17px] px-8 py-4"
            >
              {heroContent.secondaryCta}
            </button>
          </div>
          
          {/* Micro-reassurance under CTA */}
          <div className={`flex items-center gap-4 text-[13px] ${isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'}`}>
            <span className="flex items-center gap-1">
              <Sparkles size={12} strokeWidth={1.5} />
              IA + OCR
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} strokeWidth={1.5} />
              quiz gratuit
            </span>
            <span className="flex items-center gap-1">
              <Shield size={12} strokeWidth={1.5} />
              Tes données restent privées
            </span>
          </div>
        </motion.div>

        {/* Business CTA */}
        <motion.button
          onClick={handleBusiness}
          data-testid="hero-cta-business"
          className="btn-apple btn-apple-secondary mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {heroContent.businessCta}
        </motion.button>

        {/* Stats - More impactful, urgency-driven */}
        <motion.div
          className={`rounded-2xl p-6 md:p-8 ${isDark ? 'bg-[#141414]' : 'bg-[#f5f5f7]'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <p className={`text-[13px] tracking-[-0.01em] mb-4 ${isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'}`}>
            Toute l'ambition reste en arriere-plan, l'interface reste simple.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {heroStats.map((stat, index) => (
              <motion.div 
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
              >
                <p className={`text-[32px] sm:text-[40px] font-semibold tracking-[-0.02em] mb-1 ${stat.color}`}>
                  {stat.value}
                </p>
                <p className={`text-[13px] tracking-[-0.01em] ${isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'}`}>
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator - minimal */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        <motion.div
          className={`w-6 h-10 rounded-full border-2 flex justify-center pt-2 ${
            isDark ? 'border-[#3a3a3c]' : 'border-[#d2d2d7]'
          }`}
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <motion.div 
            className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-[#6e6e73]' : 'bg-[#86868b]'}`}
            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </motion.div>

      {/* Scroll indicator end */}
    </section>
  );
};

export default HeroSection;
